import type { ChangeEventHandler, CSSProperties, ReactNode } from "react";

import { sx } from "../style";

export interface CheckboxProps {
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  label?: ReactNode;
  /** Invisible accessible name for icon-only checkboxes (e.g. table rows). */
  ariaLabel?: string;
  disabled?: boolean;
  id?: string;
  style?: CSSProperties;
}

/** Checkbox with label. Supports indeterminate. */
export function Checkbox({ checked, indeterminate, onChange, label, ariaLabel, disabled, id, style }: CheckboxProps) {
  const on = checked || indeterminate;
  return (
    <label
      htmlFor={id}
      style={sx({
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? "var(--opacity-disabled)" : 1,
        ...style,
      })}
    >
      <span
        style={{
          width: 18,
          height: 18,
          flex: "none",
          borderRadius: "var(--radius-xs)",
          border: `1.5px solid ${on ? "var(--color-brand-primary)" : "var(--color-border-strong)"}`,
          background: on ? "var(--color-brand-primary)" : "var(--neutral-0)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all var(--duration-fast)",
        }}
      >
        {indeterminate ? (
          <span style={{ width: 9, height: 2, background: "#fff", borderRadius: 1 }} />
        ) : (
          checked && (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )
        )}
      </span>
      <input
        id={id}
        type="checkbox"
        checked={!!checked}
        onChange={onChange}
        disabled={disabled}
        aria-label={ariaLabel}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
      />
      {label && <span style={{ fontSize: "var(--fs-body)" }}>{label}</span>}
    </label>
  );
}

export interface RadioProps {
  checked?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  label?: ReactNode;
  name?: string;
  value?: string;
  disabled?: boolean;
  style?: CSSProperties;
}

/** Single radio option. */
export function Radio({ checked, onChange, label, name, value, disabled, style }: RadioProps) {
  return (
    <label
      style={sx({
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? "var(--opacity-disabled)" : 1,
        ...style,
      })}
    >
      <span
        style={{
          width: 18,
          height: 18,
          flex: "none",
          borderRadius: "50%",
          border: `1.5px solid ${checked ? "var(--color-brand-primary)" : "var(--color-border-strong)"}`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all var(--duration-fast)",
        }}
      >
        {checked && <span style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--color-brand-primary)" }} />}
      </span>
      <input
        type="radio"
        name={name}
        value={value}
        checked={!!checked}
        onChange={onChange}
        disabled={disabled}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
      />
      {label && <span style={{ fontSize: "var(--fs-body)" }}>{label}</span>}
    </label>
  );
}

export interface SwitchProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: ReactNode;
  /** Invisible accessible name for label-less toggles (matrix cells). */
  ariaLabel?: string;
  disabled?: boolean;
  id?: string;
  style?: CSSProperties;
}

/**
 * On/off toggle. `onChange` receives the next boolean state. Backed by a real
 * (visually-hidden) checkbox input so it is reachable by role/label in tests.
 */
export function Switch({ checked, onChange, label, ariaLabel, disabled, id, style }: SwitchProps) {
  return (
    <label
      htmlFor={id}
      style={sx({
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? "var(--opacity-disabled)" : 1,
        ...style,
      })}
    >
      <span
        style={{
          width: 36,
          height: 20,
          flex: "none",
          borderRadius: "var(--radius-pill)",
          background: checked ? "var(--color-brand-primary)" : "var(--neutral-300)",
          position: "relative",
          transition: "background var(--duration-base)",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 18 : 2,
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "var(--shadow-xs)",
            transition: "left var(--duration-base) var(--ease-out)",
          }}
        />
      </span>
      <input
        id={id}
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange?.(e.target.checked)}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
      />
      {label && <span style={{ fontSize: "var(--fs-body)" }}>{label}</span>}
    </label>
  );
}
